import React, { createContext, useContext } from "react";
import { Box, Link, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  tableTitleContainerSx,
  methodologyLinkSx,
} from "../../styles/compSheet";

const SectionHeaderContext = createContext({});

export function SectionHeaderProvider({ children, value }) {
  return (
    <SectionHeaderContext.Provider value={value || {}}>
      {children}
    </SectionHeaderContext.Provider>
  );
}

const DEFAULT_TITLE_MIN_WIDTH = 220;
const DEFAULT_RIGHT_MIN_WIDTH = 92;
const DEFAULT_HEADER_MIN_HEIGHT = 44;

function findActiveViewIcon(node) {
  if (!node) return null;
  if (Array.isArray(node)) {
    for (const child of node) {
      const icon = findActiveViewIcon(child);
      if (icon) return icon;
    }
    return null;
  }
  if (!React.isValidElement(node)) return null;

  const { options, value, children } = node.props || {};
  if (Array.isArray(options)) {
    const activeOption = options.find((option) => Object.is(option.value, value));
    if (activeOption?.icon) return activeOption.icon;
  }

  const childArray = React.Children.toArray(children);
  for (const child of childArray) {
    const icon = findActiveViewIcon(child);
    if (icon) return icon;
  }
  return null;
}

function normalizeTitleIcon(icon, theme) {
  if (!React.isValidElement(icon)) return icon;

  return React.cloneElement(icon, {
    ...icon.props,
    sx: [
      ...(Array.isArray(icon.props.sx) ? icon.props.sx : [icon.props.sx]),
      { color: theme.palette.primary.main, fontSize: 20, flexShrink: 0 },
    ].filter(Boolean),
    style: {
      ...icon.props.style,
      flexShrink: 0,
    },
  });
}

/**
 * Reusable section header for dashboard views.
 * Provides a three-column layout: title (left), view toggle (center), export button (right).
 *
 * @param {Object} props
 * @param {string} props.title - Main header text (required)
 * @param {string} props.description - Optional static description text below title
 * @param {Object} props.subtext - Optional subtext with link { text, link }
 * @param {ReactNode} props.viewToggle - View toggle component (optional)
 * @param {ReactNode} props.exportButton - Export button component (optional)
 * @param {ReactNode} props.rightContent - Additional content aligned to the right (optional)
 * @param {number|string} props.width - Container width (optional)
 * @param {string} props.maxWidth - Max width (default: 90% desktop, 98% mobile)
 * @param {ReactNode} props.titleIcon - Optional icon shown before string titles
 * @param {number|string} props.titleMinWidth - Reserved desktop title width
 * @param {number|string} props.rightMinWidth - Reserved desktop right-action width
 * @param {number|string} props.headerMinWidth - Reserved desktop header width
 * @param {number|string} props.headerMinHeight - Reserved header height
 * @param {boolean} props.noWrapTitle - Keep string titles single-line with ellipsis
 */
function SectionHeader({
  title,
  description,
  subtext,
  viewToggle,
  exportButton,
  rightContent,
  width,
  maxWidth,
  titleIcon,
  titleMinWidth,
  rightMinWidth,
  headerMinWidth,
  headerMinHeight,
  noWrapTitle = true,
}) {
  const theme = useTheme();
  const defaults = useContext(SectionHeaderContext);
  const isMobile = useMediaQuery('(max-width:480px)');
  // Medium breakpoint where buttons wrap below header
  const isMedium = useMediaQuery('(max-width:900px)');

  // Use provided maxWidth or default based on viewport
  const resolvedMaxWidth = maxWidth || (isMobile ? '98%' : '90%');
  const isStringTitle = typeof title === 'string';
  const activeViewIcon = findActiveViewIcon(viewToggle);
  const contextTitleIcon = (defaults.preferTitleIcon || isStringTitle) ? defaults.titleIcon : undefined;
  const resolvedTitleIcon = titleIcon !== undefined
    ? titleIcon
    : (defaults.preferTitleIcon
      ? (contextTitleIcon || activeViewIcon)
      : (activeViewIcon || contextTitleIcon));
  const renderedTitleIcon = normalizeTitleIcon(resolvedTitleIcon, theme);
  const resolvedTitleMinWidth = titleMinWidth ?? defaults.titleMinWidth ?? DEFAULT_TITLE_MIN_WIDTH;
  const resolvedRightMinWidth = rightMinWidth ?? defaults.rightMinWidth ?? DEFAULT_RIGHT_MIN_WIDTH;
  const resolvedHeaderMinWidth = headerMinWidth ?? defaults.headerMinWidth;
  const resolvedHeaderMinHeight = headerMinHeight ?? defaults.headerMinHeight ?? DEFAULT_HEADER_MIN_HEIGHT;

  return (
    <Box sx={{
      ...tableTitleContainerSx(theme, width, isMobile),
      width: width || '100%',
      maxWidth: resolvedMaxWidth,
      alignItems: 'center',
      minWidth: isMedium ? 0 : resolvedHeaderMinWidth,
      minHeight: resolvedHeaderMinHeight,
      mb: 1,
      py: 0.75,
      px: 1.5,
      backgroundColor: theme.table.header,
      borderRadius: '6px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      flexWrap: isMedium ? 'wrap' : 'nowrap',
    }}>
      {/* Left: Title + Description + Subtext */}
      <Box sx={{
        flex: isMedium ? '1 0 100%' : '1 1 0',
        minWidth: isMedium ? 0 : resolvedTitleMinWidth,
        overflow: 'hidden',
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 0,
          overflow: 'hidden',
        }}>
          {renderedTitleIcon && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {renderedTitleIcon}
            </Box>
          )}
          {isStringTitle ? (
            <Typography
              component="div"
              sx={{
                color: theme.palette.primary.main,
                fontSize: '18px',
                fontWeight: 500,
                lineHeight: 1.18,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: noWrapTitle ? 'ellipsis' : 'clip',
                whiteSpace: noWrapTitle ? 'nowrap' : 'normal',
              }}
            >
              {title}
            </Typography>
          ) : (
            <Box sx={{
              minWidth: 0,
              overflow: 'hidden',
              whiteSpace: noWrapTitle ? 'nowrap' : 'normal',
              '& > *': {
                minWidth: 0,
              },
              '& .MuiTypography-root': {
                overflow: noWrapTitle ? 'hidden' : undefined,
                textOverflow: noWrapTitle ? 'ellipsis' : undefined,
                whiteSpace: noWrapTitle ? 'nowrap' : undefined,
              },
            }}>
              {title}
            </Box>
          )}
        </Box>
        {(description || subtext) && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 0,
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: noWrapTitle ? 'nowrap' : 'normal',
          }}>
            {description && (
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontSize: '11px',
                fontStyle: 'italic',
                lineHeight: 1.15,
                minWidth: 0,
                overflow: 'hidden',
                pr: 0.5,
                textOverflow: 'ellipsis',
                whiteSpace: noWrapTitle ? 'nowrap' : 'normal',
              }}>
                {description}
              </Typography>
            )}
            {subtext && (
              <Tooltip title={subtext.text} arrow placement="bottom">
                <Link
                  component={RouterLink}
                  to={subtext.link}
                  underline="none"
                  aria-label={subtext.text}
                  sx={{
                    ...methodologyLinkSx(theme),
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0,
                    ml: 0,
                    lineHeight: 1,
                  }}
                >
                  <InfoOutlinedIcon sx={{
                    color: theme.palette.text.secondary,
                    fontSize: 14,
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  }} />
                </Link>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* Center: View Toggle */}
      <Box sx={{
        flex: isMedium ? '1 0 auto' : '0 0 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mx: 1,
        mt: isMedium ? 1 : 0,
        order: isMedium ? 2 : 0,
        minWidth: 0,
        flexShrink: 0,
      }}>
        {viewToggle}
      </Box>

      {/* Right: Secondary content */}
      <Box sx={{
        flex: isMedium ? '0 0 auto' : '1 1 0',
        display: 'flex',
        justifyContent: isMedium ? 'center' : 'flex-end',
        alignItems: 'center',
        gap: 1,
        minWidth: isMedium ? 0 : ((exportButton || rightContent) ? resolvedRightMinWidth : 0),
        mt: isMedium ? 1 : 0,
        order: isMedium ? 3 : 0,
        flexShrink: 0,
      }}>
        {rightContent}
        {exportButton}
      </Box>
    </Box>
  );
}

export default SectionHeader;
